import { Box, CircularProgress } from '@mui/material';
import { createFileRoute } from '@tanstack/react-router';
import { RoutePaths } from 'common-types';
import { buildRouteMeta } from 'helpers/seo';
import type { JSX } from 'react';
import { lazy, Suspense } from 'react';
import { redirectToLumiNext, shouldHandOffToLumiNext } from 'utils';

const SignIn = lazy(() => import('pages/sign-in'));

const Loader = (): JSX.Element => (
  <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
    <CircularProgress />
  </Box>
);

export const Route = createFileRoute('/_public/_empty/sign-in')({
  // Production: never show the legacy sign-in form — send users straight to
  // lumi-next, which owns authentication now. Gated on !preload so hover-preload
  // (defaultPreload: 'intent') does not navigate. The returned promise never
  // resolves, so the pending loader shows while the browser navigates away.
  beforeLoad: ({ preload }) => {
    if (!preload && shouldHandOffToLumiNext()) {
      return redirectToLumiNext({ pathname: RoutePaths.SIGN_IN });
    }
    return undefined;
  },
  pendingComponent: Loader,
  head: () =>
    buildRouteMeta({
      title: 'Lumi Login - Sign In to Your Lumi App Account',
      description:
        'Lumi login page where users can securely sign in to their Lumi app account and access personalized styling recommendations, wardrobe tools, outfit planning features, and their saved styling profile. Continue your Lumi styling journey and manage your looks, outfits, and wardrobe with the Lumi app.',
      keywords: 'lumi reviews, lumi login, lumi styling, lumi clothing, lumi clothes, lumi app, lumi',
    }),
  component: SignInWrapper,
});

function SignInWrapper(): JSX.Element {
  return (
    <Suspense fallback={<Loader />}>
      <SignIn />
    </Suspense>
  );
}
